import { AUTH_ERROR_CODES, MOCK_OTP_CODE } from '../constants/auth.constants'
import { config } from '@/config/env'
import { isPasswordSecure, isValidEmail } from '../utils/auth.utils'
import { AuthError } from './auth-error'
import { mockUsersRepo } from './mock-users.repo'

export type LoginCredentials = {
  email: string
  password: string
  rememberMe?: boolean
}

export type SignupPayload = {
  fullName: string
  email: string
  password: string
  organizationName: string
  /**
   * Collected by the form but deliberately NOT sent. SecureID's signup accepts
   * `name`, `email`, `password`, `organizationName` and `termsAccepted` and
   * nothing else, so a referral code has nowhere to go today. It stays on the
   * payload rather than being deleted from the form, so the field is one line
   * away from working once there is an endpoint that takes it.
   */
  referralCode?: string
}

/**
 * What a successful signup returns.
 *
 * Note what is absent: no access token, no refresh token, no session. SecureID
 * creates the organization and its first admin and stops there, so the client
 * has nothing to sign anyone in WITH — which is exactly why the page sends the
 * user to /login afterwards rather than to the dashboard.
 */
export type SignupResult = {
  user: {
    id: string
    email: string
    name: string
    role: string
    status: string
    tenantId: string
  }
  tenant: {
    id: string
    name: string
    slug: string
    plan: string
    status: string
    presenceId: string | null
  }
  message: string
}

/**
 * Authentication service.
 *
 * `signup` and `logout` are real. The password-reset and email-verification
 * methods below are still mocks against `mockUsersRepo` — they are wired to
 * SecureID's `/forgot-password`, `/reset-password` and OTP endpoints, but that
 * is a separate change; nothing outside signup was touched here.
 *
 * The mock session minting that used to live here (`createSession`,
 * `toAuthUser`) is gone with the mock signup. It produced `mock_access_…`
 * tokens that no server had ever issued, and the real signup returns no tokens
 * at all, so there is nothing left for it to fake.
 */
const LATENCY_MS = 700

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Map a signup failure onto the typed error the pages already branch on. */
const toSignupError = (status: number, body: any): AuthError => {
  const message: string | undefined = body?.error?.message

  // Email already registered. SecureID's own wording leaks nothing useful, so
  // the page substitutes its own and pins it to the email field.
  if (status === 409) return new AuthError(AUTH_ERROR_CODES.EMAIL_EXISTS)

  // Field-level rejection, normalised to 422 by our backend proxy. The server's
  // message names the offending field, so it beats a generic string.
  if (status === 422) {
    return new AuthError(AUTH_ERROR_CODES.VALIDATION_ERROR, message)
  }

  return new AuthError(AUTH_ERROR_CODES.UNKNOWN, message)
}

export const authService = {
  // NOTE: there is deliberately no `login(email, password)`. It existed as a
  // mock that minted `mock_access_…` / `mock_refresh_…` and wrote them into the
  // real session store, and it was still wired into the auth store even though
  // the backend has no credential-login route in any environment. SecureID OIDC
  // is the only way to obtain a session; see LoginPage + OidcCallbackPage.

  /**
   * Create a SecureID organization and its first `tenant_admin` user.
   *
   * Goes through our own backend (`/api/v1/auth/signup`) rather than straight
   * to SecureID: SecureID only returns `access-control-allow-origin` for
   * `http://localhost:5173`, and the dev server runs over https, so a direct
   * call is refused by CORS before it is even sent. Same-origin here, proxied
   * server-side.
   *
   * `termsAccepted` is hard-coded `true` because the form cannot reach this
   * call without both consent boxes ticked — the submit button stays disabled
   * and `validate()` blocks it. Sending the literal SecureID demands keeps the
   * two in step; if a consent box is ever made optional, this must become the
   * real value rather than staying a lie the server cannot detect.
   */
  async signup(payload: SignupPayload): Promise<SignupResult> {
    let res: Response
    try {
      res = await fetch(`${config.apiUrl}/api/v1/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.fullName.trim(),
          email: payload.email.trim().toLowerCase(),
          password: payload.password,
          // Omitted rather than sent blank when the user left it empty: the
          // backend decides "did they name one" on this field alone, and an
          // empty string is a weaker signal than its absence.
          ...(payload.organizationName.trim()
            ? { organizationName: payload.organizationName.trim() }
            : {}),
          termsAccepted: true,
        }),
      })
    } catch {
      // Offline, DNS, TLS — never reached the server, so nothing was created.
      throw new AuthError(AUTH_ERROR_CODES.UNKNOWN, 'Could not reach the server. Check your connection and try again.')
    }

    const body = await res.json().catch(() => null)

    if (!res.ok) throw toSignupError(res.status, body)

    return body as SignupResult
  },

  async requestPasswordReset(email: string): Promise<void> {
    await sleep(LATENCY_MS)
    const target = email.trim().toLowerCase()
    if (!isValidEmail(target) || !mockUsersRepo.exists(target)) {
      throw new AuthError(AUTH_ERROR_CODES.EMAIL_NOT_FOUND)
    }
    // Real backend would send an OTP email here.
  },

  async resetPassword(
    email: string,
    otp: string,
    newPassword: string,
  ): Promise<void> {
    await sleep(LATENCY_MS)
    if (otp !== MOCK_OTP_CODE) {
      throw new AuthError(AUTH_ERROR_CODES.INVALID_OTP)
    }
    if (!isPasswordSecure(newPassword)) {
      throw new AuthError(AUTH_ERROR_CODES.WEAK_PASSWORD)
    }
    const updated = mockUsersRepo.updatePassword(email, newPassword)
    if (!updated) {
      throw new AuthError(AUTH_ERROR_CODES.EMAIL_NOT_FOUND)
    }
  },

  async changePassword(
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await sleep(LATENCY_MS)

    const target = email.trim().toLowerCase()
    if (!isValidEmail(target) || !mockUsersRepo.exists(target)) {
      throw new AuthError(AUTH_ERROR_CODES.EMAIL_NOT_FOUND)
    }
    if (!mockUsersRepo.verifyPassword(target, currentPassword)) {
      throw new AuthError(AUTH_ERROR_CODES.INVALID_PASSWORD)
    }
    if (!isPasswordSecure(newPassword)) {
      throw new AuthError(AUTH_ERROR_CODES.WEAK_PASSWORD)
    }
    const updated = mockUsersRepo.updatePassword(target, newPassword)
    if (!updated) {
      throw new AuthError(AUTH_ERROR_CODES.EMAIL_NOT_FOUND)
    }
  },

  async verifyEmail(email: string, otp: string): Promise<void> {
    await sleep(LATENCY_MS)
    if (otp !== MOCK_OTP_CODE) {
      throw new AuthError(AUTH_ERROR_CODES.INVALID_OTP)
    }
    if (!mockUsersRepo.exists(email)) {
      throw new AuthError(AUTH_ERROR_CODES.EMAIL_NOT_FOUND)
    }
    // No-op for mock — user is auto-verified on creation.
  },

  /**
   * Real sign-out. This used to be `await sleep(150)` — a mock left over from
   * before the backend existed — so signing out never reached our own server,
   * let alone SecureID's revocation endpoint. The refresh token stayed live
   * upstream until its 30-day idle expiry (guide section 9).
   *
   * `credentials: 'include'` carries the HttpOnly session cookie, which is what
   * identifies the session to revoke. Failure is swallowed on purpose: local
   * state is cleared either way and a user must always be able to sign out.
   *
   * With `redirectUri`, also asks for SecureID's own logout page (see the
   * store's `logout`) — null when the server could not give one.
   */
  async logout(redirectUri?: string): Promise<{ logoutUrl: string; state: string } | null> {
    try {
      const res = await fetch(`${config.apiUrl}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(redirectUri ? { redirectUri } : {}),
      })
      const data = (await res.json().catch(() => null)) as { logoutUrl?: unknown; state?: unknown } | null
      if (typeof data?.logoutUrl === 'string' && typeof data.state === 'string') {
        return { logoutUrl: data.logoutUrl, state: data.state }
      }
    } catch {
      /* best-effort; the session cookie is cleared server-side when reachable */
    }
    return null
  },
}
